import { useCallback, useEffect, useState } from "react";
import { api, fmtTRY, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowUpRight, ArrowDownRight, Wallet, ArrowRightLeft } from "lucide-react";

const periods = [
  { key: "gunluk", label: "Günlük" },
  { key: "haftalik", label: "Haftalık" },
  { key: "aylik", label: "Aylık" },
  { key: "yillik", label: "Yıllık" },
  { key: "aralik", label: "Tarih Aralığı" },
];

const toISO = (d) => d.toISOString().slice(0, 10);

function rangeFor(key) {
  const now = new Date();
  const end = toISO(now);
  const start = new Date(now);
  if (key === "gunluk") return { start: end, end };
  if (key === "haftalik") {
    start.setDate(now.getDate() - 6);
    return { start: toISO(start), end };
  }
  if (key === "aylik") {
    start.setDate(now.getDate() - 29);
    return { start: toISO(start), end };
  }
  if (key === "yillik") {
    start.setDate(now.getDate() - 364);
    return { start: toISO(start), end };
  }
  return { start: end, end };
}

export default function SummarySection() {
  const [period, setPeriod] = useState("aylik");
  const [customStart, setCustomStart] = useState(toISO(new Date()));
  const [customEnd, setCustomEnd] = useState(toISO(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeRange, setActiveRange] = useState(null);

  const fetchSummary = useCallback(async (start, end) => {
    setLoading(true);
    try {
      const res = await api.get("/summary", { params: { start, end } });
      setData(res.data);
      setActiveRange({ start, end });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (period !== "aralik") {
      const { start, end } = rangeFor(period);
      fetchSummary(start, end);
    }
  }, [period, fetchSummary]);

  const runCustom = () => {
    if (customStart > customEnd) return;
    fetchSummary(customStart, customEnd);
  };

  const cards = data
    ? [
        { label: "Toplam Gelir", value: data.total_gelir, icon: ArrowUpRight, cls: "text-emerald-600", bg: "bg-emerald-50", count: data.gelir_count },
        { label: "Toplam Gider", value: data.total_gider, icon: ArrowDownRight, cls: "text-rose-600", bg: "bg-rose-50", count: data.gider_count },
        { label: "Net Kâr / Zarar", value: data.net, icon: Wallet, cls: data.net >= 0 ? "text-indigo-600" : "text-rose-600", bg: "bg-indigo-50", count: null },
        { label: "Transfer", value: data.transfer_total, icon: ArrowRightLeft, cls: "text-blue-600", bg: "bg-blue-50", count: data.transfer_count },
      ]
    : [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2" data-testid="period-toggles">
        {periods.map((p) => (
          <button
            key={p.key}
            data-testid={`period-${p.key}`}
            onClick={() => setPeriod(p.key)}
            className={`rounded-lg px-4 py-2 text-sm font-medium border transition-colors ${
              period === p.key
                ? "bg-indigo-600 border-indigo-600 text-white"
                : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {period === "aralik" && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-end gap-4">
          <div className="space-y-1.5">
            <Label>Başlangıç</Label>
            <Input
              data-testid="range-start-input"
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              className="w-44"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Bitiş</Label>
            <Input
              data-testid="range-end-input"
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              className="w-44"
            />
          </div>
          <Button
            data-testid="run-range-btn"
            onClick={runCustom}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            Özet Çıkar
          </Button>
        </div>
      )}

      {activeRange && (
        <p className="text-sm text-slate-500" data-testid="active-range-label">
          {fmtDate(activeRange.start)} — {fmtDate(activeRange.end)} arası özet
          {loading && " · yükleniyor..."}
        </p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="summary-cards">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div
              key={c.label}
              className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-[0.16em] font-semibold text-slate-500">
                  {c.label}
                </span>
                <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${c.bg}`}>
                  <Icon className={`h-4 w-4 ${c.cls}`} />
                </div>
              </div>
              <div className={`mt-4 text-2xl font-bold tracking-tight tabular-nums ${c.cls}`}>
                {fmtTRY(c.value)}
              </div>
              {c.count !== null && (
                <p className="text-xs text-slate-400 mt-1">{c.count} işlem</p>
              )}
            </div>
          );
        })}
      </div>

      {data && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 text-sm text-slate-600" data-testid="summary-total-count">
          Bu dönemde toplam <span className="font-semibold text-slate-900">{data.transaction_count}</span> işlem kaydedildi.
        </div>
      )}
    </div>
  );
}
